import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import Layout from 'components/Layout';
import { useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import { legacyGridSafeContainerSx, legacyGridSafeItemSx } from 'theme/legacyGridInterop';
import Button from '@mui/material/Button';
import Title from 'components/Title';
import { Link } from 'react-router-dom';
import { Invoice } from 'components/Reservations';
import Comments from 'components/Comments';
import { InvoiceItems, InvoicePayments, AuthorizationHoldItems } from 'components/Invoice';
import { AccountStatementWidget } from 'components/AccountStatement';
import Logs from 'components/Logs';
import { InvoiceActions } from 'state/modules/invoice';
import Images from 'components/Images';
import { buildUrl } from 'utils';
import get from 'lodash/get';
import Can from 'components/Can';
import { useConfirm } from 'hooks';

const InvoiceDetails = (props) => {
  const { isStatementEntry } = props;
  const { id } = useParams();
  const dispatch = useDispatch();
  const invoice = useSelector((state) => state.invoice?.invoice);
  const loading = useSelector((state) => state.invoice?.loading);
  const fetchingSendEmailInvoice = useSelector((state) => state.invoice?.fetchingSendEmailInvoice);
  const fetchingExportInvoice = useSelector((state) => state.invoice?.fetchingExportInvoice);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const currentOrg = useSelector((state) => state.auth?.organizations?.current);

  const exportInvoice = () => {
    dispatch(InvoiceActions.fetchExportInvoice(id));
  };

  const sendInvoiceByEmail = (subject) => {
    dispatch(InvoiceActions.fetchSendInvoiceByEmail(id, { subject }));
  };

  const onProcessBalance = () => {
    dispatch(InvoiceActions.fetchProcessInvoiceById(id));
  };

  const onPublishInvoice = () => {
    dispatch(InvoiceActions.fetchPublishInvoice());
  };

  const [toggleSendInvoice, SendInvoice] = useConfirm({
    title: i18n.t('invoice.submit_title'),
    message: i18n.t('invoice.submit_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.submit'),
    onConfirm: onPublishInvoice,
  });

  const showProcessButton = sharingType !== 'FFB';
  const accountStatement = invoice?.account_statement;
  const showEmailControls = invoice?.receivable_type !== 'statement';
  const showSubmitButton =
    invoice?.is_draft === true && currentOrg?.id === invoice?.account_emitter?.holder_id;
  const showAuthholdItems = invoice?.authorization_holds?.length > 0;

  return (
    <Layout title={i18n.t(isStatementEntry ? 'statement_entries.page_title' : 'invoice.title')}>
      <SendInvoice />
      <Container sx={{ minWidth: '100%', margin: 0 }}>
        <Grid container spacing={2} sx={legacyGridSafeContainerSx}>
          <Grid size={12}>
            <Grid
              container
              spacing={2}
              sx={legacyGridSafeContainerSx}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid>
                <Title
                  title={i18n.t(
                    isStatementEntry ? 'statement_entries.details_title' : 'invoice.details',
                    {
                      id,
                    }
                  )}
                />
              </Grid>
              <Grid>
                {showSubmitButton && (
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={toggleSendInvoice}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {i18n.t('common.submit')}
                  </Button>
                )}
                <Button
                  component={Link}
                  to={buildUrl(
                    isStatementEntry
                      ? `/statement-entries/${id}/preview`
                      : `/invoices/${id}/preview`
                  )}
                  variant="outlined"
                  color="primary"
                  style={{ textTransform: 'capitalize', marginLeft: 10 }}
                  data-cy="invoice-preview-btn"
                >
                  {i18n.t(isStatementEntry ? 'statement_entries.preview' : 'invoice.preview')}
                </Button>
                {showEmailControls ? (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => sendInvoiceByEmail('customer')}
                      color="primary"
                      disabled={fetchingSendEmailInvoice}
                      data-cy="invoice-send-customer-btn"
                      style={{ textTransform: 'capitalize', marginLeft: 10 }}
                    >
                      {i18n.t('invoice.send_customer')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => sendInvoiceByEmail('requester')}
                      color="primary"
                      disabled={fetchingSendEmailInvoice}
                      data-cy="invoice-send-requester-btn"
                      style={{ textTransform: 'capitalize', marginLeft: 10 }}
                    >
                      {i18n.t('invoice.send_requester')}
                    </Button>
                  </>
                ) : null}
                <Button
                  variant="contained"
                  onClick={exportInvoice}
                  color="primary"
                  disabled={fetchingExportInvoice}
                  data-cy="invoice-download-btn"
                  style={{ textTransform: 'capitalize', marginLeft: 10 }}
                >
                  {i18n.t('common.download')}
                </Button>
                {showProcessButton &&
                  !invoice?.is_draft &&
                  get(invoice, 'invoiced', 0) -
                    get(invoice, 'paid_card', 0) -
                    get(invoice, 'paid_credit', 0) !==
                    0 && (
                    <Can I="process" a="receivable">
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        style={{ textTransform: 'capitalize', marginLeft: 10 }}
                        onClick={onProcessBalance}
                      >
                        {i18n.t('invoice.process_balance')}
                      </Button>
                    </Can>
                  )}
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={2} sx={legacyGridSafeContainerSx}>
              <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={legacyGridSafeItemSx}>
                <Grid container spacing={2} sx={legacyGridSafeContainerSx}>
                  <Grid size={12}>
                    <Invoice id={id} isStatementEntry={isStatementEntry} />
                  </Grid>
                  {accountStatement?.id ? (
                    <Grid size={12}>
                      <AccountStatementWidget
                        accountStatement={accountStatement}
                        loading={loading}
                      />
                    </Grid>
                  ) : null}
                  {isStatementEntry ? null : (
                    <Grid size={12}>
                      <InvoicePayments id={id} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={legacyGridSafeItemSx}>
                <Grid container spacing={2} sx={legacyGridSafeContainerSx}>
                  <Grid size={12}>
                    <InvoiceItems id={id} isStatementEntry={isStatementEntry} />
                  </Grid>

                  {showAuthholdItems && (
                    <Grid size={12}>
                      <AuthorizationHoldItems />
                    </Grid>
                  )}

                  <Grid size={12}>
                    <Images
                      id={id}
                      title={i18n.t('common.images')}
                      sourceType="invoice"
                      noProfile
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={legacyGridSafeItemSx}>
                <Grid container spacing={2} sx={legacyGridSafeContainerSx}>
                  <Grid size={12}>
                    <Comments type="invoice" id={+id} />
                  </Grid>
                  <Grid size={12}>
                    <Logs
                      targetId={parseInt(id)}
                      targetType="Invoice"
                      title={i18n.t(isStatementEntry ? 'statement_entries.logs' : 'invoice.logs')}
                      height={439}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

InvoiceDetails.propTypes = {
  isStatementEntry: PropTypes.any,
};

export { InvoiceDetails };
